// Basic
import PropTypes from 'prop-types';
// Store & actions
import actionsMerchantAccounts from '../../../api/actionsMerchantAccounts';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
// Common
import ICONS from '../../../../../../common-utils/enums/icons';
import MERCHANT_ACCOUNT_STATUS from '../../../enums/merchantAccountStatus';
import merchantAccountsFields from '../../../fields/merchantAccountFields';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import Permissions from '../../../../../permissions/Permissions';
import { useToast } from '@/common-utils/hooks/use-toast';
// Lang
import { nsAccount } from '@/app/i18n/namespaces';

/**
 * @param merchantAccount
 * @param permissions
 * @param setLaunchRefetch
 * @returns {JSX} ActivateDeactivate
 */
function ActivateDeactivate(merchantAccount, permissions, setLaunchRefetch) {
    const merchantAccountId = merchantAccount.uuid;

    // Toast
    const { toast } = useToast();

    // API Calls
    const onSuccessActivateMerchantAccount = () => {
        toast({
            description: <Entity entity={'toast.success.activate'} namespace={nsAccount} />,
            variant: 'success'
        });
        setLaunchRefetch(true);
    };
    const onErrorActivateMerchantAccount = () => {
        toast({
            description: <Entity entity={'toast.error.activate'} namespace={nsAccount} />,
            variant: 'danger'
        });
    };
    const submitActivateMerchantAccount = actionsMerchantAccounts.activateMerchantAccount(onSuccessActivateMerchantAccount, onErrorActivateMerchantAccount);

    const onSuccessDeactivateMerchantAccount = () => {
        toast({
            description: <Entity entity={'toast.success.deactivate'} namespace={nsAccount} />,
            variant: 'success'
        });
        setLaunchRefetch(true);
    };
    const onErrorDeactivateMerchantAccount = () => {
        toast({
            description: <Entity entity={'toast.error.deactivate'} namespace={nsAccount} />,
            variant: 'danger'
        });
    };
    const submitDeactivateMerchantAccount = actionsMerchantAccounts.deactivateMerchantAccount(onSuccessDeactivateMerchantAccount, onErrorDeactivateMerchantAccount);

    const activateMerchantAccount = () => {
        submitActivateMerchantAccount.mutate(merchantAccountId);
        toast({
            description: <Entity entity={'toast.loading'} />
        });
    };
    const deactivateMerchantAccount = () => {
        submitDeactivateMerchantAccount.mutate(merchantAccountId);
        toast({
            description: <Entity entity={'toast.loading'} />
        });
    };
    const merchantAccountStatusIsOneOf = (statuses) => {
        // Abort the case where the user is not loaded yet or where the statuses are invalid
        if (!merchantAccount || !statuses) {
            return false;
        }

        const merchantAccountStatus = merchantAccount[merchantAccountsFields.STATUS.key];
        return statuses.some(status => merchantAccountStatus === status.name);
    };

    return (
        [{
            icon: ICONS.SUBSCRIPTION_ACTIVATE,
            entity: 'actions.row.activate',
            namespace: nsAccount,
            className: 'text-success',
            onClick: activateMerchantAccount,
            visible: merchantAccountStatusIsOneOf([MERCHANT_ACCOUNT_STATUS.INACTIVE]) &&
                !!activateMerchantAccount,
            disabled: !hasRequiredPermissions({ permissions, requiredPermissions: [Permissions.CONTRACTS_ACCESS_ACTIVATE.name] }),
            tooltipForbidden: 'title.permissions.access_denied'

        }, {
            icon: ICONS.SUBSCRIPTION_TERMINATE,
            entity: 'actions.row.deactivate',
            namespace: nsAccount,
            className: 'text-warning',
            onClick: deactivateMerchantAccount,
            visible: merchantAccountStatusIsOneOf([MERCHANT_ACCOUNT_STATUS.ACTIVE]) && !!deactivateMerchantAccount,
            disabled: !hasRequiredPermissions({ permissions, requiredPermissions: [Permissions.CONTRACTS_ACCESS_DEACTIVATE.name] }),
            tooltipForbidden: 'title.permissions.access_denied'
        }
        ]);
}
ActivateDeactivate.propTypes = {
    setLaunchRefetch: PropTypes.func.isRequired,
    merchantAccount: PropTypes.object.isRequired,
    permissions: PropTypes.array.isRequired
};

export default ActivateDeactivate;